import type { ReactNode } from 'react';
import { Icon, type IconName } from '../theme/Icon';
import { InfoTip } from './InfoTip';
import s from './PageHeader.module.css';
import { t } from '@app/i18n';

export interface Breadcrumb {
  label: string;
  href?: string;
}

export interface PageHeaderProps {
  title: string;
  subtitle?: ReactNode;
  icon?: IconName;
  breadcrumbs?: Breadcrumb[];
  /**
   * "Atualizado às …" — linha discreta abaixo do subtítulo. Aceita nó para a parte da linha que tem estado próprio (a
   * hora da foto do cluster em T4, com `data-state`, aim-6ph.13).
   */
  updatedLabel?: ReactNode;
  /** Explicação ⓘ ao lado do título (FR-132). */
  tip?: string;
  /** Ações da página. Um único botão primário por seção (FR-131). */
  actions?: ReactNode;
  /** Badges/chips ao lado do título (estado da entidade). */
  badges?: ReactNode;
}

/** Cabeçalho de tela (`.bc` + `.ph`). */
export function PageHeader({
  title,
  subtitle,
  icon,
  breadcrumbs,
  updatedLabel,
  tip,
  actions,
  badges,
}: PageHeaderProps) {
  return (
    <>
      {breadcrumbs?.length ? (
        <nav aria-label={t('common.ui.breadcrumb')}>
          <ol className={s.bc}>
            {breadcrumbs.map((item, i) => {
              const last = i === breadcrumbs.length - 1;
              return (
                <li key={`${item.label}-${i}`}>
                  {item.href && !last ? <a href={item.href}>{item.label}</a> : null}
                  {!item.href || last ? last ? <b>{item.label}</b> : item.label : null}
                  {!last ? (
                    <Icon name="chevron" size="xs" className={s.sep} aria-hidden="true" />
                  ) : null}
                </li>
              );
            })}
          </ol>
        </nav>
      ) : null}

      <div className={s.ph}>
        {icon ? (
          <span className={s.picon} aria-hidden="true">
            <Icon name={icon} size="xl" />
          </span>
        ) : null}

        <div className={s.titleWrap}>
          <h1 className={s.title}>
            {title}
            {tip ? <InfoTip text={tip} label={t('common.ui.about', { label: title })} /> : null}
            {badges}
          </h1>
          {subtitle ? <p className={s.sub}>{subtitle}</p> : null}
          {updatedLabel ? <p className={s.upd}>{updatedLabel}</p> : null}
        </div>

        {actions ? <div className={s.act}>{actions}</div> : null}
      </div>
    </>
  );
}
